import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import { ThemeProvider } from './contexts/ThemeContext';
import { I18nProvider } from './i18n/I18nContext';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, DashboardLayout, AdminLayout } from './components/Layouts';

import Landing from './pages/public/Landing';
import { PublicMarkets, HowItWorks, SIBotPublic, FAQPage, PublicSupport, Legal } from './pages/public/Pages';
import { Login, Register, ForgotPassword, ResetPassword, VerifyEmail } from './pages/auth/Auth';
import Onboarding from './pages/auth/Onboarding';

import Overview from './pages/dashboard/Overview';
import DashMarkets from './pages/dashboard/Markets';
import Trading from './pages/dashboard/Trading';
import SIBot from './pages/dashboard/SIBot';
import Orders from './pages/dashboard/Orders';
import Wallet from './pages/dashboard/Wallet';
import Deposits from './pages/dashboard/Deposits';
import Withdrawals from './pages/dashboard/Withdrawals';
import Settlements from './pages/dashboard/Settlements';
import History from './pages/dashboard/History';
import Notifications from './pages/dashboard/Notifications';
import Settings from './pages/dashboard/Settings';
import Security from './pages/dashboard/Security';
import DashSupport from './pages/dashboard/Support';

import AdminOverview from './pages/admin/AdminOverview';
import AdminUsers from './pages/admin/AdminUsers';
import AdminDeposits from './pages/admin/AdminDeposits';
import AdminAllocations from './pages/admin/AdminAllocations';
import AdminWithdrawals from './pages/admin/AdminWithdrawals';
import AdminSettlements from './pages/admin/AdminSettlements';
import AdminAutomation from './pages/admin/AdminAutomation';
import AdminTrading from './pages/admin/AdminTrading';
import AdminExchange from './pages/admin/AdminExchange';
import AdminAuditLogs from './pages/admin/AdminAuditLogs';

export default function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" theme="dark" richColors closeButton />
            <Routes>
              {/* Public */}
              <Route path="/" element={<Landing />} />
              <Route path="/markets" element={<PublicMarkets />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/si-bot" element={<SIBotPublic />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/support" element={<PublicSupport />} />
              <Route path="/terms" element={<Legal kind="terms" />} />
              <Route path="/privacy" element={<Legal kind="privacy" />} />
              <Route path="/risk-disclosure" element={<Legal kind="risk-disclosure" />} />

              {/* Auth */}
              <Route path="/register" element={<Register />} />
              <Route path="/login" element={<Login />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* User */}
              <Route element={<ProtectedRoute />}>
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/dashboard" element={<DashboardLayout />}>
                  <Route index element={<Overview />} />
                  <Route path="markets" element={<DashMarkets />} />
                  <Route path="trading" element={<Trading />} />
                  <Route path="si-bot" element={<SIBot />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="wallet" element={<Wallet />} />
                  <Route path="deposits" element={<Deposits />} />
                  <Route path="withdrawals" element={<Withdrawals />} />
                  <Route path="settlements" element={<Settlements />} />
                  <Route path="history" element={<History />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="security" element={<Security />} />
                  <Route path="support" element={<DashSupport />} />
                </Route>
              </Route>

              {/* Admin */}
              <Route element={<ProtectedRoute admin />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminOverview />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="deposits" element={<AdminDeposits />} />
                  <Route path="allocations" element={<AdminAllocations />} />
                  <Route path="withdrawals" element={<AdminWithdrawals />} />
                  <Route path="settlements" element={<AdminSettlements />} />
                  <Route path="automation" element={<AdminAutomation />} />
                  <Route path="trading" element={<AdminTrading />} />
                  <Route path="exchange" element={<AdminExchange />} />
                  <Route path="audit-logs" element={<AdminAuditLogs />} />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
